/** NPC source window adapted from the existing PC twin; all rule kernels stay shared. */
import type { ItemFieldRow } from './item-field-reader';
import type { FromBaseRow } from './from-base-context';
type FromBasePlayerInput = FromBaseRow;
import { fromBaseClone, fromBaseRecord, fromBaseUuid } from './from-base-context';

import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { foldCreatureDefenseEntries } from './creature-defense';
import { DEFENSE_SLOTS, deriveDamageSourceKind, STATUS_DAMAGE_DEFENSE_LAYERS } from './creature-defense-delta';
import { readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';

import { appendPhpCompileArray } from './status-row-construction';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { meetsAbilityLevelRequirements } from './ability-level-requirement-gate';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readCreatureStatuses } from './creature-status-reader';
import { isAuraPassive } from './passive-integer-fold';
import { compileAbilityGrantedCreatureFlags } from './ability-granted-creature-flags';
import { resolveLayerEntry } from './layer-entry-resolver';
import { createFormulaReadContext } from './formula-read-context';

/** Preserve native keyed stores while sharing the first-wins fold across windows. */
export function defenseWriter(host: FromBaseRow, readers: ReturnType<typeof createFromBaseNpcGrantContext>['readers']) {
    const storedValue = (row: FromBaseRow, key: string) => readers.itemField({ [key]: [row[key] ?? null] }, key);
    const storedEntries = (row: FromBaseRow, key: string): unknown[] => {
        const bucket = storedValue(row, key);
        return bucket !== null && typeof bucket === 'object' ? Object.values(bucket) : [];
    };
    const add = (key: string, id: string, source: FromBaseRow): void => {
        if (!id) return;
        const descriptor = DEFENSE_SLOTS[key];
        const creature = descriptor ? (((host.computed ??= {}) as FromBaseRow).creature ??= {}) as FromBaseRow : null;
        const target = creature ? (creature.defenses ??= {}) as FromBaseRow : host, slot = descriptor?.slot ?? key;
        const initial = storedValue(target, slot);
        const folded = foldCreatureDefenseEntries({ family: descriptor?.key === 'item_layer_effect' ? 'status' : 'damage',
            initialEntries: storedEntries(target, slot), events: [{ uuid: id, source }] });
        const keys = initial !== null && typeof initial === 'object' ? Object.keys(initial) : [];
        let output: ItemFieldRow = keys.every((key, index) => key === String(index)) ? folded.slice(0, keys.length)
            : Object.fromEntries(keys.map((key, index) => [key, folded[index]]));
        for (const entry of folded.slice(keys.length)) output = appendPhpCompileArray(output, entry);
        target[slot] = output;
    };
    return { storedValue, storedEntries, add };
}

/** Native root/permanent seeding, before feat/passive grants. */
export function constructNpcDefenseSeedsFromBase(base: FromBasePlayerInput, metadata: import('./from-base-player-resources').FromBasePlayerResourceMetadata, input: FromBaseRow): FromBaseRow {
    const host = fromBaseClone(input);
    const { readers, definition } = createFromBaseNpcGrantContext(base, metadata, host, 'defenses-flags-ac-capacity');
    const ownReaders = createFormulaReadContext({ catalogRows: {}, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: () => 'host_wins', resolveCarrierRef: () => null, resolveInventoryInstance: () => null });
    const entries = (row: ItemFieldRow, key: string) => Object.values(row === host ? ownReaders.layerEntries(row, key) : readers.layerEntries(row, key));
    const { storedValue, storedEntries, add } = defenseWriter(host, readers);
    // Raw definition construction retains authored layers in its bucket; the
    // native finalizer receives those same cells mirrored onto the root.
    const rootEntries = (key: string) => host[key] == null ? Object.values(ownReaders.layerEntries(host, key)) : storedEntries(host, key);
    const self = { source_item_id: readers.itemField(host, 'item_id'), source_name: readers.itemField(host, 'item_name'),
        source_kind: deriveDamageSourceKind(phpJsonString(readers.itemField(host, 'item_type'))) ?? readers.itemField(host, 'item_type') };
    const damage = (key: string, entry: unknown, fallback: FromBaseRow): void => {
        if (fromBaseRecord(entry)) {
            const id = phpJsonString(storedValue(entry, 'damage_type') ?? '');
            const source = Object.fromEntries(['source_item_id', 'source_name', 'source_kind', 'source_origin_item_id', 'source_origin_name']
                .map(name => [name, storedValue(entry, name)]));
            add(key, id, source);
            for (const contributor of storedEntries(entry, 'all_sources')) if (fromBaseRecord(contributor)) add(key, id, contributor);
        } else if (typeof entry === 'string' && entry !== '') add(key, entry, fallback);
    };
    // pl_cost_layer_entry_uuid's extra numeric-cell fallback follows uuid-slot.
    const costId = (entry: unknown) => {
        if (typeof entry === 'string') return entry.trim();
        const direct = readItemUuidSlot(entry);
        if (direct || !fromBaseRecord(entry) && !Array.isArray(entry)) return direct;
        return readItemUuidSlot(readers.layerValue(entry, '0')) || readItemUuidSlot(readers.layerValue(entry, 'item_id'));
    };
    const damageKeys = [...STATUS_DAMAGE_DEFENSE_LAYERS];
    for (const key of ['spell_damage_resistance', 'spell_damage_vulnerability', ...damageKeys, 'immune_status_effect', 'status_effect_permanent']) {
        const values = rootEntries(key);
        if (!values.length) continue;
        if (damageKeys.includes(key as typeof damageKeys[number])) {
            delete host[key];
            if (fromBaseRecord(host.item_layer_types)) delete host.item_layer_types[key];
            for (const entry of values) damage(key, entry, self);
        } else {
            const seen = new Set<string>();
            host[key] = values.filter(entry => { const id = costId(entry); if (!id || seen.has(id)) return false; seen.add(id); return true; });
        }
    }
    for (const key of damageKeys.map(key => 'object_' + key)) {
        const values = rootEntries(key);
        if (!values.length) continue;
        host[key] = [];
        if (fromBaseRecord(host.item_layer_types)) delete host.item_layer_types[key];
        for (const entry of values) damage(key, entry, self);
    }
    for (const entry of entries(host, 'immune_status_effect')) add('immune_status_effect', readLayerListEntryUuid(entry), self);
    for (const entry of entries(host, 'status_effect_permanent')) {
        const id = readLayerListEntryUuid(entry);
        if (!fromBaseUuid(id)) continue;
        const nested = fromBaseRecord(entry) ? storedValue(entry, 'item_arr') : null;
        const catalog = storedValue(host, 'catalog');
        const riding = fromBaseRecord(catalog) ? storedValue(catalog, id) : null;
        const ridingLayers = fromBaseRecord(riding) ? storedValue(riding, 'item_layer_types') : null;
        const status = nested !== null && typeof nested === 'object' ? nested as ItemFieldRow
            : fromBaseRecord(riding) && ridingLayers !== null && typeof ridingLayers === 'object' && Object.keys(ridingLayers).length ? riding : definition(id);
        if (!status || !Object.keys(status).length) continue;
        const source = { source_item_id: id, source_name: readers.itemField(status, 'item_name') ?? 'Permanent Status Effect', source_kind: 'status_effect' };
        for (const key of damageKeys) for (const value of entries(status, key)) add(key,
            fromBaseRecord(value) && storedValue(value, 'damage_type') != null
                ? phpJsonString(storedValue(value, 'damage_type')) : readLayerListEntryUuid(value), source);
        for (const value of entries(status, 'immune_status_effect')) add('immune_status_effect', readLayerListEntryUuid(value), source);
    }
    for (const key of ['contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect'])
        for (const entry of entries(host, key)) add(key, readLayerListEntryUuid(entry), self);
    return host;
}

/** Native feat/passive fold and status-root retirement, before AC/capacity. */
export function constructNpcDefenseGrantsFromBase(base: FromBasePlayerInput, metadata: import('./from-base-player-resources').FromBasePlayerResourceMetadata, input: FromBaseRow): FromBaseRow {
    const host = fromBaseClone(input);
    const { readers, ownReaders, definition, deps } = createFromBaseNpcGrantContext(base, metadata, host, 'defenses-flags-ac-capacity');
    const { storedValue, storedEntries, add } = defenseWriter(host, readers);
    const entries = (row: ItemFieldRow, key: string) => Object.values((row === host ? ownReaders : readers).layerEntries(row, key));
    const has = (row: ItemFieldRow, key: string) => readers.layerValue(row, key) !== null;
    const conditions = createAbilityConditionEvaluator(deps);
    const pc = fromBaseRecord(host.player_characters) ? host.player_characters : null;
    const levels = new Map<string, number>();
    if (pc) for (let slot = 1; slot <= 20; slot++) {
        if (!phpJsonTruthy(readers.layerValue(pc, `class_${slot}`))) continue;
        for (const key of [`class_${slot}`, `subclass_${slot}`]) {
            const id = phpJsonString(readers.layerValue(pc, key));
            if (fromBaseUuid(id)) levels.set(id.toLowerCase(), phpJsonInt(readers.layerValue(pc, `class_level_${slot}`)));
        }
    }
    const levelGate = (row: ItemFieldRow, source: string) => {
        const requirement = (key: string) => {
            const value = readers.layerValue(row, key);
            return value == null || value === '' ? null : phpJsonInt(value);
        };
        const statusDelivered = source !== '' && Object.values(readCreatureStatuses(host, 'target', definition)).some(status =>
            fromBaseRecord(status) && phpJsonString(readers.layerValue(status, 'status_id')).toLowerCase() === source.toLowerCase());
        return meetsAbilityLevelRequirements({ isPlayerCharacter: pc !== null, totalLevel: phpJsonInt(readers.layerValue(host, 'total_level')),
            requiresLevel: requirement('requires_level'), requiresClassLevel: requirement('requires_class_level'),
            sourceClassLevel: levels.get(source.toLowerCase()) ?? null, statusDelivered });
    };
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: definition });
    const carriers = new Map<string, string>();
    if (fromBaseRecord(host.player_characters)) for (const provider of providers.providers(host)) {
        const source = phpJsonString(readers.itemField(provider, 'item_id')).toLowerCase();
        if (!source) continue;
        for (const container of ['feat', 'passive_ability']) for (const entry of entries(provider, container)) {
            const id = readLayerListEntryUuid(entry).toLowerCase();
            if (id && !carriers.has(id)) carriers.set(id, source);
        }
    }
    const sourceOf = (entry: unknown): string => {
        const source = fromBaseRecord(entry) ? storedValue(entry, 'source') : '';
        return typeof source === 'string' && source !== '' ? source : carriers.get(readLayerListEntryUuid(entry).toLowerCase()) ?? '';
    };
    const resolve = (entry: unknown): ItemFieldRow | null => {
        const nested = fromBaseRecord(entry) ? storedValue(entry, 'item_arr') : null;
        if (nested !== null && typeof nested === 'object' && Object.keys(nested).length) return nested as ItemFieldRow;
        const id = readLayerListEntryUuid(entry);
        return fromBaseUuid(id) ? definition(id) : null;
    };
    const allowed = (row: ItemFieldRow, source: string) => !isAuraPassive(row, readers.layerValue)
        && conditions.check(row, host, false, false) && levelGate(row, source);
    const granted: FromBaseRow[] = [], seen = new Set<string>();
    for (const entry of entries(host, 'feat')) {
        const nested = fromBaseRecord(entry) ? storedValue(entry, 'item_arr') : null;
        const row = nested !== null && typeof nested === 'object' && Object.keys(nested).length
            ? nested as ItemFieldRow : resolveLayerEntry(entry, host, definition), source = sourceOf(entry);
        if (!fromBaseRecord(row) && !Array.isArray(row)) continue;
        const layers = readers.itemField(row, 'item_layer_types');
        if (!fromBaseRecord(layers) || !phpJsonTruthy(storedValue(layers, 'passive_ability')) || !allowed(row, source)) continue;
        for (const child of entries(row, 'passive_ability')) {
            const id = readLayerListEntryUuid(child).toLowerCase(), key = id + '|' + source.toLowerCase();
            if (!id || seen.has(key)) continue;
            const passive = resolveLayerEntry(child, host, definition);
            if ((!fromBaseRecord(passive) && !Array.isArray(passive)) || !phpJsonTruthy(readers.itemField(passive, 'item_layer_types'))) continue;
            seen.add(key);
            granted.push({ item_arr: passive, source, ft_granted_by_feat: readers.itemField(row, 'item_id') ?? readLayerListEntryUuid(entry) });
        }
    }
    let critTotal = 0, integerGranted: Set<string> | undefined;
    delete host.critical_hit_range_increase_mod_from_passives;
    for (const container of ['feat', 'passive_ability']) for (const entry of [...entries(host, container), ...(container === 'passive_ability' ? granted : [])]) {
        const row = resolve(entry);
        if (!row || isAuraPassive(row, readers.layerValue)) continue;
        const id = phpJsonString(readers.itemField(row, 'item_id') ?? readLayerListEntryUuid(entry));
        if (has(row, 'triggers_after_combat_begins') && has(row, 'duration_type')
            && !Object.values(readCreatureStatuses(host, 'target', definition)).some(status => {
                if (!fromBaseRecord(status)) return false;
                const held = readers.layerValue(status, 'status_item_id') ?? readers.itemField(status, 'item_id');
                return typeof held === 'string' && held.toUpperCase() === id.toUpperCase();
            })) continue;
        if (!allowed(row, sourceOf(entry))) continue;
        const source = { source_item_id: id || null, source_name: readers.itemField(row, 'item_name'), source_kind: container };
        for (const key of STATUS_DAMAGE_DEFENSE_LAYERS) for (const value of entries(row, key)) add(key,
            fromBaseRecord(value) && storedValue(value, 'damage_type') != null
                ? phpJsonString(storedValue(value, 'damage_type')) : readLayerListEntryUuid(value), source);
        for (const key of ['immune_status_effect', 'contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect'])
            for (const value of entries(row, key)) add(key, readLayerListEntryUuid(value), source);
        const flags = compileAbilityGrantedCreatureFlags({ providers: [{
            ignoreSpellComponentVerbal: has(row, 'ignore_spell_component_verbal'), ignoreSpellComponentSomatic: has(row, 'ignore_spell_component_somatic'),
            ignoreSpellComponentMaterial: has(row, 'ignore_spell_component_material'), rangedWeaponAttacksIgnoreCover: has(row, 'ranged_weapon_attacks_ignore_cover'),
            rangedSpellAttacksIgnoreCover: has(row, 'ranged_spell_attacks_ignore_cover'), rangedAttacksIgnoreCover: has(row, 'ranged_attacks_ignore_cover'),
            bodyVanishesAtZeroVitality: has(row, 'body_vanishes_at_zero_vitality'),
        }] });
        Object.assign(host, flags.root);
        if (Object.keys(flags.computedMeta).length) {
            const creature = (((host.computed ??= {}) as FromBaseRow).creature ??= {}) as FromBaseRow;
            Object.assign(creature.meta ??= {}, flags.computedMeta);
        }
        for (const key of ['ranged_attacks_ignore_partial_cover', 'adv_vs_own_marked_target', 'crit_to_hit',
            'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach', 'ignore_ranged_spell_attack_roll_dis_within_an_enemy_reach',
            'ignore_long_range_attack_roll_dis', 'attacks_ignore_damage_mitigation', 'opportunity_attack_roll_adv', 'overrun_roll_adv',
            'ranged_attacks_do_not_reveal_position', 'not_detected_by_truesight', 'not_detected_by_special_senses', 'ignore_all_attack_roll_dis',
            'attacks_against_cannot_gain_adv', 'spells_ignore_damage_mitigation', 'damage_ignores_all_damage_mitigation', 'concentration_roll_is_rolled_at_max'])
            if (has(row, key)) host[key] = [1];
        for (const key of ['ignore_damage_type_resistance', 'skill_roll_ignore_dis', 'ignore_damage_mitigation_damage_type'])
            if (has(row, key)) host[key] = [...new Set([...storedEntries(host, key), ...entries(row, key)]
                .map(value => readLayerListEntryUuid(value).toUpperCase()).filter(Boolean))];
        for (const key of ['critical_hit_damage_multiplier', 'damage_to_objects_multiplier', 'see_through_walls_max_thickness_ft']) {
            if (!has(row, key)) continue;
            const value = phpJsonInt(readers.layerValue(row, key));
            if (value > phpJsonInt(storedValue(host, key))) host[key] = value;
        }
        if (container === 'passive_ability' && !phpJsonTruthy(fromBaseRecord(entry) ? readers.layerValue(entry, 'ft_granted_by_feat') : null)
            && has(row, 'critical_hit_range_increase_mod')) {
            integerGranted ??= new Set(createDynamicIntegerSourceCollector({ ...deps, metadata: metadata.rollup,
                inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes }).resolvedFeats(host)
                .flatMap(feat => storedEntries(feat as FromBaseRow, 'granted')).map(child => phpJsonString(readers.itemField(child as ItemFieldRow, 'item_id')).toLowerCase()));
            if (!integerGranted.has(id.toLowerCase())) {
                critTotal += Math.max(0, phpJsonInt(phpJsonString(readers.layerValue(row, 'critical_hit_range_increase_mod')).replace(/^[ +]+|[ +]+$/g, '')));
                host.critical_hit_range_increase_mod_from_passives = critTotal;
            }
        }
    }
    for (const key of ['immune_status_effect', 'contesting_roll_adv_vs_status_effect', 'contesting_roll_dis_vs_status_effect']) {
        delete host[key];
        if (fromBaseRecord(host.item_layer_types)) delete host.item_layer_types[key];
    }
    return host;
}
